import { useState } from 'react'
import { NavLink, Outlet, useLocation } from 'react-router-dom'
import {
  CalendarDays, CheckSquare2, ChevronRight, CircleDollarSign, FolderKanban,
  Gauge, Handshake, LogOut, Menu, PackageOpen, ShieldAlert, Target, UserRoundPlus, X,
} from 'lucide-react'
import { useAuth } from '../auth/AuthContext'
import { classNames } from '../lib/formatters'

const navigation = [
  { label: 'Dashboard', to: '/', icon: Gauge },
  { label: 'Leads', to: '/leads', icon: UserRoundPlus },
  { label: 'Opportunities', to: '/opportunities', icon: Target },
  { label: 'Calendar', to: '/calendar', icon: CalendarDays },
  { label: 'Quotes', to: '/quotes', icon: CircleDollarSign },
  { label: 'Projects', to: '/projects', icon: FolderKanban },
  { label: 'Suppliers', to: '/suppliers', icon: Handshake },
  { label: 'Tasks', to: '/tasks', icon: CheckSquare2 },
  { label: 'Issues', to: '/issues', icon: ShieldAlert },
]

function Brand() {
  return (
    <img
      src="/termovista-logo.png"
      alt="TermoVista"
      className="h-auto w-[198px] max-w-full object-contain object-left"
    />
  )
}

function SidebarContent({ onNavigate, username, onLogout }: { onNavigate?: () => void; username: string | null; onLogout: () => void }) {
  return (
    <>
      <div className="px-5 pb-8 pt-6"><Brand /></div>
      <nav className="flex-1 space-y-1 px-3" aria-label="Main navigation">
        {navigation.map(({ label, to, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            end={to === '/'}
            onClick={onNavigate}
            className={({ isActive }) => classNames(
              'group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
              isActive ? 'bg-white/10 text-white' : 'text-pine-100/70 hover:bg-white/[.06] hover:text-white',
            )}
          >
            {({ isActive }) => (
              <>
                <Icon className={classNames('h-[18px] w-[18px]', isActive ? 'text-ember-400' : 'text-pine-300/70 group-hover:text-pine-200')} strokeWidth={1.9} />
                <span>{label}</span>
                {isActive && <ChevronRight className="ml-auto h-4 w-4 text-pine-400" />}
              </>
            )}
          </NavLink>
        ))}
      </nav>
      <div className="mx-3 mb-4 mt-6 border-t border-white/10 pt-4">
        <NavLink
          to="/products"
          onClick={onNavigate}
          className={({ isActive }) => classNames(
            'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
            isActive ? 'bg-white/10 text-white' : 'text-pine-100/70 hover:bg-white/[.06] hover:text-white',
          )}
        >
          <PackageOpen className="h-[18px] w-[18px] text-pine-300/70" strokeWidth={1.9} /> Products
        </NavLink>
      </div>
      <div className="border-t border-white/10 p-4">
        <div className="flex items-center gap-3 rounded-lg px-2 py-1.5">
          <img
            src="/termovista-icon.png"
            alt=""
            className="h-9 w-9 rounded-xl object-cover ring-1 ring-white/15"
          />
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-semibold text-white">{username || 'TermoVista user'}</div>
            <div className="text-xs text-pine-200/60">Authorised session</div>
          </div>
          <button
            type="button"
            onClick={onLogout}
            className="rounded-lg p-2 text-pine-300/60 transition hover:bg-white/10 hover:text-white"
            title="Sign out"
            aria-label="Sign out"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </>
  )
}

export function AppShell() {
  const [open, setOpen] = useState(false)
  const { username, logout } = useAuth()
  const location = useLocation()
  const section = navigation.find((item) => item.to !== '/' && location.pathname.startsWith(item.to))?.label
    ?? (location.pathname.startsWith('/products') ? 'Products' : 'Dashboard')

  return (
    <div className="min-h-screen bg-[#f7f8f5] lg:pl-[248px]">
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-[248px] flex-col bg-pine-950 lg:flex">
        <SidebarContent username={username} onLogout={() => void logout()} />
      </aside>

      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <button className="absolute inset-0 bg-slate-950/45 backdrop-blur-sm" aria-label="Close navigation" onClick={() => setOpen(false)} />
          <aside className="relative flex h-full w-[278px] flex-col bg-pine-950 shadow-2xl">
            <button className="absolute right-3 top-3 rounded-lg p-2 text-pine-200 hover:bg-white/10" onClick={() => setOpen(false)} aria-label="Close navigation"><X className="h-5 w-5" /></button>
            <SidebarContent onNavigate={() => setOpen(false)} username={username} onLogout={() => void logout()} />
          </aside>
        </div>
      )}

      <div className="min-h-screen">
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-slate-200/80 bg-white/90 px-4 backdrop-blur-md sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <button className="rounded-lg p-2 text-slate-600 hover:bg-slate-100 lg:hidden" onClick={() => setOpen(true)} aria-label="Open navigation"><Menu className="h-5 w-5" /></button>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-pine-600">Workspace</div>
              <div className="text-sm font-semibold text-slate-800">{section}</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden items-center gap-2 rounded-full bg-pine-50 px-3 py-1.5 text-xs font-semibold text-pine-700 sm:flex">
              <span className="h-1.5 w-1.5 rounded-full bg-pine-500" /> Internal access
            </div>
            <img
              src="/termovista-icon.png"
              alt="TermoVista user"
              className="h-9 w-9 rounded-xl object-cover shadow-sm ring-1 ring-pine-950/10"
            />
          </div>
        </header>
        <main className="mx-auto w-full max-w-[1600px] px-4 py-6 sm:px-6 lg:px-8 lg:py-8"><Outlet /></main>
      </div>
    </div>
  )
}
